/* eslint-disable consistent-return */
import {
  useRef,
  useEffect,
  useState,
  useCallback,
  PropsWithChildren,
  useId,
  ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Button, Heading, Icon } from '@alto-avios/alto-ui'
import { useMediaQuery } from 'usehooks-ts'
import { deviceSizes } from '../../theme/deviceSizes'
import { MobileModalProps } from './MobileModal.types'
import styles from './MobileModal.module.scss'

// Implementation based on Next.js documentation https://github.com/vercel/next.js/tree/canary/examples/with-portals.

type ClientOnlyPortalProps = {
  selector: string
  children?: ReactNode
}

function ClientOnlyPortal({ children, selector }: ClientOnlyPortalProps) {
  const ref = useRef<Element | null>(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    ref.current = document.querySelector(selector)
    setMounted(true)
  }, [selector])

  return mounted && ref.current && children ? createPortal(children as any, ref.current) : null
}

function MobileModal({
  id,
  title,
  heading,
  children,
  isModalVisible,
  handleModalClose,
  disablePlaceholder,
  allowScroll,
  placeholder,
  forceModal,
}: PropsWithChildren<MobileModalProps>) {
  const isTabletOrSmaller = useMediaQuery(`(max-width: ${deviceSizes.medium})`)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const dialogRef = useRef<HTMLElement | null>(null)
  const titleId = useId()

  useEffect(() => {
    const originalOverflow = document.body.style.overflow ?? ''
    if (isModalVisible && !allowScroll) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = originalOverflow
    }
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isModalVisible, allowScroll])

  useEffect(() => {
    let timer: number | undefined

    if (isModalVisible) {
      previousFocusRef.current = document.activeElement as HTMLElement
      timer = window.setTimeout(() => {
        dialogRef.current?.focus()
      }, 0)
    }

    return () => {
      if (timer !== undefined) {
        window.clearTimeout(timer)
      }
      if (previousFocusRef.current && document.contains(previousFocusRef.current)) {
        previousFocusRef.current.focus()
      }
    }
  }, [isModalVisible])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleModalClose()
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) {
          return
        }
        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    },
    [handleModalClose]
  )

  useEffect(() => {
    if (isModalVisible) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isModalVisible, handleKeyDown])

  if ((!forceModal && !isTabletOrSmaller) || !isModalVisible) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{placeholder || children}</>
  }

  return (
    <>
      {!disablePlaceholder && (placeholder || children)}
      <ClientOnlyPortal selector="#modal">
        <section
          ref={dialogRef}
          id={id}
          data-testid="mobile-modal"
          className={classNames(styles.mobileModal, 'site-MobileModal')}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ?? titleId}
          tabIndex={-1}
        >
          <Button aria-label="Close modal" styleVariant="white" onPress={handleModalClose}>
            <Icon iconName="xmark" iconSize="2x" />
          </Button>
          {heading && (
            <Heading as="h4" className={styles.heading} id={titleId}>
              {heading}
            </Heading>
          )}
          {children}
        </section>
      </ClientOnlyPortal>
    </>
  )
}

export default MobileModal
